import type { RefObject } from 'react';
import { useEffect, useMemo } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { iframely } from '#src/app/shared/utils/iframely';

import type { MediaHitEmbed } from '../../../../shared/context';
import { useMediaHitsContext } from '../../../../shared/context';
import { useMediaHitsPermissions } from '../../../../shared/hooks/useMediaHitsPermissions';

type Result = ModelHookResult<{
	showSection: boolean;
	showList: boolean;
	showAdd: boolean;
	/** Whether there are any media hits to load embeds for (used to decide whether to render the lazy-load trigger). */
	hasMediaHits: boolean;
	/** Whether more media hits exist beyond the initial preview page, i.e. whether "see all" has anything to show. */
	showSeeAll: boolean;
	trackingData: {
		petition_id: string;
	};
}>;

export function useMediaHits(modalLoadedEmbedsRef?: RefObject<readonly MediaHitEmbed[]>): Result {
	const {
		data: { mediaEmbeds },
	} = useMediaHitsContext();
	const {
		data: { canAdd },
	} = useMediaHitsPermissions();
	const {
		data: {
			petition: { id: petitionId },
			mediaHitsPage: { isLastPage },
		},
	} = usePetitionDetailsPageData();
	const track = useTracking();

	useEffect(() => {
		if (!mediaEmbeds.length) return undefined;

		return iframely.on('click', (href) => {
			const theClickedEmbed =
				mediaEmbeds.find((m) => m.url === href) ?? modalLoadedEmbedsRef?.current.find((m) => m.url === href);
			void track('petition_media_feed_click', {
				url: href,
				petition_id: petitionId,
				media_source: theClickedEmbed?.mediaSource,
			});
		});
	}, [track, mediaEmbeds, petitionId, modalLoadedEmbedsRef]);

	return useMemo(() => {
		// Only show the list once at least one embed has resolved successfully, so non-staff users never see an empty
		// media section when every embed fails to render.
		const hasValidEmbed = mediaEmbeds.some((embed) => embed.status === 'valid');
		const showList = canAdd || hasValidEmbed;
		const showAdd = canAdd;

		const showSection = showList || showAdd;
		return {
			data: {
				showList,
				showAdd,
				showSection,
				hasMediaHits: mediaEmbeds.length > 0,
				showSeeAll: !isLastPage,
				trackingData: { petition_id: petitionId },
			},
			actions: {},
		};
	}, [canAdd, mediaEmbeds, petitionId, isLastPage]);
}
